import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Title} from '@angular/platform-browser';
import {NavigationEnd, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {combineLatest, Observable} from 'rxjs';
import {filter, map, startWith} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {NavigationPageEnum} from '../../../features/user-access/models/navigations.enums';
import {selectSelectedStore} from '../../../features/user-access/user-stores-store/user-store.selectors';
import {IStore} from '../../shared-models/store/store';

const ENVIRONMENT_TITLE_SUFFIXES: ReadonlyMap<string, string> = new Map<string, string>([
  ['Manage IT', 'MI'],
  ['Dev', 'Dev'],
  ['QA', 'QA'],
]);

/**
 * Pages that report on every store the user has access to, so naming a single store in the tab title
 * would misrepresent what is on screen.
 */
const PAGES_WITHOUT_STORE_IN_TITLE: ReadonlySet<string> = new Set<string>([
  NavigationPageEnum.Home,
  NavigationPageEnum.Dashboard,
  NavigationPageEnum.DashboardReporting,
]);

/**
 * Owns the browser tab title. The title is always rebuilt from the base title in `index.html` plus the
 * environment, the selected store and the unread notification count, so the parts can change
 * independently without the previously rendered title being parsed back apart.
 */
@Injectable({providedIn: 'root'})
export class DocumentTitleService {
  private readonly titleService = inject(Title);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  private readonly baseTitle: string = this.titleService.getTitle();

  private storeName = '';
  private unreadNotificationCount = 0;

  constructor() {
    combineLatest([this.currentUrl(), this.store.select(selectSelectedStore)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([url, selectedStore]: [string, IStore | undefined]) => {
        this.storeName = this.resolveStoreName(url, selectedStore);
        this.applyTitle();
      });
  }

  /**
   * Shows the number of unread notifications at the end of the tab title, or removes it when nothing is
   * unread.
   */
  setUnreadNotificationCount(unreadNotificationCount: number): void {
    this.unreadNotificationCount = unreadNotificationCount;
    this.applyTitle();
  }

  private currentUrl(): Observable<string> {
    return this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
      startWith(this.router.url),
    );
  }

  /**
   * Resolves the store name to display for a URL, which is nothing at all on the pages that span every
   * store.
   */
  private resolveStoreName(url: string, selectedStore?: IStore): string {
    const page: string = url.split('?')[0].split('/').filter(Boolean)[0] ?? NavigationPageEnum.Home;
    if (PAGES_WITHOUT_STORE_IN_TITLE.has(page)) {
      return '';
    }
    return selectedStore?.name ?? '';
  }

  private applyTitle(): void {
    const parts: string[] = [this.baseTitle];

    const environmentSuffix: string = ENVIRONMENT_TITLE_SUFFIXES.get(environment.environment);
    if (environmentSuffix) {
      parts.push(environmentSuffix);
    }

    if (this.storeName) {
      parts.push(this.storeName);
    }

    const unreadSuffix: string = this.unreadNotificationCount > 0 ? ` (${this.unreadNotificationCount})` : '';
    this.titleService.setTitle(`${parts.join(' - ')}${unreadSuffix}`);
  }
}
